import { mutationOptions, queryOptions } from '@tanstack/react-query'
import { UserListModel } from 'src/models/user.models'
import { QueryError } from 'src/queries/network.utils'
import {
  AddSecondaryEmailRequest,
  AddSecondaryEmailResponse,
  DeleteSecondaryEmailRequest,
  DeleteSecondaryEmailResponse,
  ResendSecondaryEmailVerificationRequest,
  VerifySecondaryEmailRequest,
} from 'src/queries/secondaryEmails/secondaryEmails.models'
import {
  addSecondaryEmail,
  deleteSecondaryEmail,
  getSecondaryEmails,
  resendSecondaryEmailVerification,
  verifySecondaryEmail,
} from 'src/queries/secondaryEmails/secondaryEmails.network'

/**
 * Typed query-key factory colocated with the factories. The family is
 * self-contained (no cross-feature invalidation), so the keys live here rather
 * than in `queries.constants.ts`.
 */
export const secondaryEmailsQueryKeys = {
  all: () => ['secondaryEmails'] as const,
  list: () => [...secondaryEmailsQueryKeys.all(), 'list'] as const,
}

export const secondaryEmailsQueries = {
  list: () =>
    queryOptions({
      queryKey: secondaryEmailsQueryKeys.list(),
      queryFn: getSecondaryEmails,
    }),
}

/**
 * mutationOptions() factories — the analog of queryOptions() for mutations.
 * Each is a typed passthrough that bundles the network `mutationFn`. Components
 * consume them with `useMutation({ ...factory(), onSuccess })` and own their
 * invalidation/side effects, because the success behavior differs per call
 * site (the popup-step transition, modal close, and toast are component
 * concerns; only verify + delete refetch the list).
 */
export const secondaryEmailsMutations = {
  add: () =>
    mutationOptions<
      AddSecondaryEmailResponse,
      QueryError,
      AddSecondaryEmailRequest
    >({
      mutationFn: addSecondaryEmail,
    }),
  verify: () =>
    mutationOptions<UserListModel, QueryError, VerifySecondaryEmailRequest>({
      mutationFn: verifySecondaryEmail,
    }),
  resendVerification: () =>
    mutationOptions<
      UserListModel,
      QueryError,
      ResendSecondaryEmailVerificationRequest
    >({
      mutationFn: resendSecondaryEmailVerification,
    }),
  delete: () =>
    mutationOptions<
      DeleteSecondaryEmailResponse,
      QueryError,
      DeleteSecondaryEmailRequest
    >({
      mutationFn: deleteSecondaryEmail,
    }),
}
